import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { COGNITIVE_DISTORTIONS, EMOTIONS } from '@/constants/cognitiveDistortions';
import { EmotionWheel } from './EmotionWheel';
import { BodySensationMapper } from './BodySensationMapper';
import { Brain, Sparkles, Save, MessageCircle, Scale, ChevronDown, Lightbulb, Target, Zap } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';

interface ThoughtEntryFormProps {
  onSave: (data: ThoughtData) => Promise<void>;
  onChallenge: (thought: string, distortions: string[]) => void;
  onStartReframe?: () => void;
}

export interface ThoughtData {
  thought_content: string;
  situation: string;
  emotion: string;
  emotion_intensity: number;
  cognitive_distortions: string[];
  alternative_thought: string;
  // New evidence-based fields
  evidence_for?: string;
  evidence_against?: string;
  body_sensations?: Array<{
    region: string;
    sensations: string[];
    intensity: number;
  }>;
  behaviour_response?: string;
  emotion_category?: string;
}

export function EnhancedThoughtEntryForm({ onSave, onChallenge, onStartReframe }: ThoughtEntryFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState<'basic' | 'evidence' | 'body' | 'reframe'>('basic');
  const [useEmotionWheel, setUseEmotionWheel] = useState(false);
  const [formData, setFormData] = useState<ThoughtData>({
    thought_content: '',
    situation: '',
    emotion: '',
    emotion_intensity: 5,
    cognitive_distortions: [],
    alternative_thought: '',
    evidence_for: '',
    evidence_against: '',
    body_sensations: [],
    behaviour_response: '',
    emotion_category: ''
  });

  const toggleDistortion = (distortionId: string) => {
    setFormData(prev => ({
      ...prev,
      cognitive_distortions: prev.cognitive_distortions.includes(distortionId)
        ? prev.cognitive_distortions.filter(d => d !== distortionId)
        : [...prev.cognitive_distortions, distortionId]
    }));
  };

  const handleSave = async () => {
    if (!formData.thought_content.trim()) {
      toast({
        title: "Please enter a thought",
        description: "Describe the thought you want to log",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData);
      toast({
        title: "Thought logged",
        description: "Great self-awareness! Your thought has been recorded."
      });
      // Reset form
      setFormData({
        thought_content: '',
        situation: '',
        emotion: '',
        emotion_intensity: 5,
        cognitive_distortions: [],
        alternative_thought: '',
        evidence_for: '',
        evidence_against: '',
        body_sensations: [],
        behaviour_response: '',
        emotion_category: ''
      });
      setActiveSection('basic');
    } catch (error) {
      toast({
        title: "Error saving thought",
        description: "Please try again",
        variant: "destructive"
      });
    }
    setIsSubmitting(false);
  };

  const handleChallengeClick = () => {
    if (!formData.thought_content.trim()) {
      toast({
        title: "Please enter a thought first",
        description: "Describe the thought you want to challenge with AI",
        variant: "destructive"
      });
      return;
    }
    onChallenge(formData.thought_content, formData.cognitive_distortions);
  };

  const handleEmotionSelect = (emotion: string, category: string) => {
    setFormData(prev => ({ 
      ...prev, 
      emotion: emotion.charAt(0).toUpperCase() + emotion.slice(1),
      emotion_category: category 
    }));
  };

  const completionScore = [
    formData.thought_content,
    formData.situation,
    formData.emotion,
    formData.cognitive_distortions.length > 0,
    formData.evidence_for || formData.evidence_against,
    formData.alternative_thought
  ].filter(Boolean).length;

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-gradient-to-br from-primary/20 to-accent/20">
              <Brain className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle>Log a Thought</CardTitle>
              <CardDescription>
                Capture and examine your thoughts to build awareness
              </CardDescription>
            </div>
          </div>
          {/* Completion indicator */}
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              {[1,2,3,4,5,6].map(i => (
                <div 
                  key={i}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i <= completionScore ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-muted-foreground">{completionScore}/6</span>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Section Navigation */}
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'basic', label: 'Basic', icon: Brain },
            { id: 'evidence', label: 'Evidence', icon: Scale },
            { id: 'body', label: 'Body', icon: Target },
            { id: 'reframe', label: 'Reframe', icon: Lightbulb }
          ].map(section => (
            <Button
              key={section.id}
              variant={activeSection === section.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveSection(section.id as typeof activeSection)}
              className="gap-1"
            >
              <section.icon className="w-3 h-3" />
              {section.label}
            </Button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Basic Section */}
          {activeSection === 'basic' && (
            <motion.div
              key="basic"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              {/* Situation */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">What was happening?</Label>
                <Textarea
                  placeholder="Briefly describe the situation that triggered this thought..."
                  value={formData.situation}
                  onChange={(e) => setFormData(prev => ({ ...prev, situation: e.target.value }))}
                  className="min-h-[60px]"
                />
              </div>

              {/* The Thought */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">The Thought <span className="text-destructive">*</span></Label>
                <Textarea
                  placeholder="What went through your mind? Write the exact thought..."
                  value={formData.thought_content}
                  onChange={(e) => setFormData(prev => ({ ...prev, thought_content: e.target.value }))}
                  className="min-h-[80px] border-primary/30 focus:border-primary"
                />
              </div>

              {/* Emotion Selection */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">How did you feel?</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setUseEmotionWheel(!useEmotionWheel)}
                    className="text-xs"
                  >
                    {useEmotionWheel ? 'Simple Select' : 'Emotion Wheel'}
                  </Button>
                </div>

                {useEmotionWheel ? (
                  <EmotionWheel
                    selectedEmotion={formData.emotion}
                    selectedIntensity={formData.emotion_intensity}
                    onEmotionSelect={handleEmotionSelect}
                    showBodyMapping={false}
                    showNeeds={false}
                  />
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    <Select 
                      value={formData.emotion} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, emotion: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select emotion" />
                      </SelectTrigger>
                      <SelectContent>
                        {EMOTIONS.map(emotion => (
                          <SelectItem key={emotion} value={emotion}>{emotion}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <div className="space-y-1">
                      <Label className="text-xs">Intensity: {formData.emotion_intensity}/10</Label>
                      <Slider
                        value={[formData.emotion_intensity]}
                        onValueChange={([value]) => setFormData(prev => ({ ...prev, emotion_intensity: value }))}
                        min={1}
                        max={10}
                        step={1}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Cognitive Distortions */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">
                  Identify Thinking Patterns
                  <span className="text-muted-foreground font-normal ml-2">(tap all that apply)</span>
                </Label>
                <div className="flex flex-wrap gap-2">
                  {COGNITIVE_DISTORTIONS.map(distortion => (
                    <Badge
                      key={distortion.id}
                      variant={formData.cognitive_distortions.includes(distortion.id) ? "default" : "outline"}
                      className="cursor-pointer transition-all hover:scale-105"
                      style={formData.cognitive_distortions.includes(distortion.id) ? { backgroundColor: distortion.colour } : {}}
                      onClick={() => toggleDistortion(distortion.id)}
                    >
                      {distortion.name}
                    </Badge>
                  ))}
                </div>
                
                {formData.cognitive_distortions.length > 0 && (
                  <div className="mt-3 p-3 rounded-lg bg-accent/10 border border-accent/20">
                    <p className="text-sm font-medium mb-2">Selected patterns:</p>
                    {formData.cognitive_distortions.map(id => {
                      const d = COGNITIVE_DISTORTIONS.find(cd => cd.id === id);
                      return d ? (
                        <div key={id} className="text-xs text-muted-foreground mb-1">
                          <strong>{d.name}:</strong> {d.description}
                        </div>
                      ) : null;
                    })}
                  </div>
                )}
              </div>

              {/* Behaviour Response */}
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full justify-between">
                    <span className="flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      What did you do? (Behaviour)
                    </span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <Textarea
                    placeholder="How did you respond or behave after this thought?"
                    value={formData.behaviour_response}
                    onChange={(e) => setFormData(prev => ({ ...prev, behaviour_response: e.target.value }))}
                    className="min-h-[60px]"
                  />
                </CollapsibleContent>
              </Collapsible>
            </motion.div>
          )}

          {/* Evidence Section */}
          {activeSection === 'evidence' && (
            <motion.div
              key="evidence"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                <div className="flex items-start gap-2">
                  <Scale className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm">Weigh the Evidence</h4>
                    <p className="text-xs text-muted-foreground">
                      Like a scientist or judge, examine the facts for and against your thought. 
                      Focus on objective evidence, not feelings or interpretations.
                    </p>
                  </div>
                </div>
              </div>

              {formData.thought_content && (
                <div className="p-3 rounded-lg bg-muted/50 border">
                  <p className="text-xs text-muted-foreground mb-1">The thought being examined:</p>
                  <p className="text-sm italic">"{formData.thought_content}"</p>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                {/* Evidence For */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    Evidence Supporting
                  </Label>
                  <Textarea
                    placeholder="What facts support this thought being true? (Be specific, use evidence not feelings)"
                    value={formData.evidence_for}
                    onChange={(e) => setFormData(prev => ({ ...prev, evidence_for: e.target.value }))}
                    className="min-h-[120px] border-red-500/30"
                  />
                  <p className="text-xs text-muted-foreground">
                    Tip: Ask "Would this hold up in court?"
                  </p>
                </div>

                {/* Evidence Against */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    Evidence Against
                  </Label>
                  <Textarea
                    placeholder="What facts contradict this thought? What would a friend say? What are you overlooking?"
                    value={formData.evidence_against}
                    onChange={(e) => setFormData(prev => ({ ...prev, evidence_against: e.target.value }))}
                    className="min-h-[120px] border-green-500/30"
                  />
                  <p className="text-xs text-muted-foreground">
                    Tip: Think of times when the opposite was true
                  </p>
                </div>
              </div>

              {/* Helpful prompts */}
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-muted-foreground">Questions to consider:</span>
                {[
                  'What would I tell a friend?',
                  'Is this fact or interpretation?',
                  'What am I ignoring?',
                  'Has the opposite ever been true?'
                ].map(prompt => (
                  <Badge key={prompt} variant="secondary" className="text-xs">
                    {prompt}
                  </Badge>
                ))}
              </div>
            </motion.div>
          )}

          {/* Body Section */}
          {activeSection === 'body' && (
            <motion.div
              key="body"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <BodySensationMapper
                onSensationsChange={(sensations) => setFormData(prev => ({ 
                  ...prev, 
                  body_sensations: sensations 
                }))}
                initialSensations={formData.body_sensations}
                compact
              />
            </motion.div>
          )}

          {/* Reframe Section */}
          {activeSection === 'reframe' && (
            <motion.div
              key="reframe"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm">Create a Balanced Alternative</h4>
                    <p className="text-xs text-muted-foreground">
                      This isn't about being positive - it's about being accurate. 
                      Based on the evidence, what's a more balanced way to view this?
                    </p>
                  </div>
                </div>
              </div>

              {/* Show evidence summary if available */}
              {(formData.evidence_for || formData.evidence_against) && (
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  {formData.evidence_for && (
                    <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                      <p className="text-xs font-medium text-red-600 mb-1">Evidence for:</p>
                      <p className="text-muted-foreground text-xs">{formData.evidence_for}</p>
                    </div>
                  )}
                  {formData.evidence_against && (
                    <div className="p-3 rounded-lg bg-green-500/5 border border-green-500/20">
                      <p className="text-xs font-medium text-green-600 mb-1">Evidence against:</p>
                      <p className="text-muted-foreground text-xs">{formData.evidence_against}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm font-medium">Alternative Thought</Label>
                <Textarea
                  placeholder="Based on the evidence, write a more balanced perspective. Include the negatives if real, but also the positives..."
                  value={formData.alternative_thought}
                  onChange={(e) => setFormData(prev => ({ ...prev, alternative_thought: e.target.value }))}
                  className="min-h-[100px] border-amber-500/30"
                />
              </div>

              {onStartReframe && (
                <Button
                  variant="outline"
                  onClick={onStartReframe}
                  className="w-full border-amber-500/30 hover:bg-amber-500/10"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Try Guided Reframing Exercise
                </Button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
          <Button 
            onClick={handleSave} 
            disabled={isSubmitting || !formData.thought_content.trim()}
            className="flex-1"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSubmitting ? 'Saving...' : 'Save Thought'}
          </Button>
          <Button 
            variant="outline"
            onClick={handleChallengeClick}
            disabled={!formData.thought_content.trim()}
            className="flex-1 border-primary/30 hover:bg-primary/10"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Challenge with Therappi AI
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
